import React, { useContext, useEffect, useState } from "react";
import { FiClock, FiGlobe, FiPlus, FiBookOpen, FiUser, FiEdit2, FiTrash2, FiImage } from "react-icons/fi";
import AppShell from "../components/layout/AppShell";
import { AuthContext } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { Card, Badge, Button, EmptyState, Spinner, Modal, Input, Textarea, SearchInput } from "../components/ui";
import { getCourses, createCourse, updateCourse, deleteCourse } from "../api/courses";
import uploadUrl from "../utils/assetUrl";
import { useNavigate } from "react-router-dom";

export default function Courses() {
  const { isAdmin } = useContext(AuthContext);
  const { notify } = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", duration: "", category: "", tutor: "", languages: "English" });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getCourses().then(res => setCourses(res.data?.courses || [])).catch(() => notify("Couldn't load courses.", "error")).finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = courses.filter(c => !query || c.title.toLowerCase().includes(query.toLowerCase()) || c.category?.toLowerCase().includes(query.toLowerCase()));

  const openCreate = () => { setModal("new"); setForm({ title: "", description: "", duration: "", category: "", tutor: "", languages: "English" }); setImageFile(null); };
  const openEdit = (c) => { setModal(c); setForm({ title: c.title, description: c.description || "", duration: c.duration || "", category: c.category || "", tutor: c.tutor || "", languages: c.languages || "English" }); setImageFile(null); };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => fd.append(k, v));
      if (imageFile) fd.append("image", imageFile);
      if (modal === "new") { await createCourse(fd); notify("Course created.", "success"); }
      else { await updateCourse(modal.id, fd); notify("Course updated.", "success"); }
      setModal(null);
      load();
    } catch { notify("Couldn't save course.", "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: "Delete this course?", description: "This will permanently delete the course along with every learner's enrollment and progress. This cannot be undone." }))) return;
    try { await deleteCourse(id); notify("Course deleted.", "success"); load(); }
    catch { notify("Couldn't delete course.", "error"); }
  };

  return (
    <AppShell title="Learning" subtitle="Grow your skills with curated courses" wide>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput value={query} onChange={setQuery} placeholder="Search courses…" className="sm:max-w-xs" />
        {isAdmin && <Button onClick={openCreate}><FiPlus className="h-4 w-4" /> Add course</Button>}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner className="h-8 w-8" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<FiBookOpen className="h-6 w-6" />}
          title={query ? "No courses match your search" : "No courses yet"}
          description={query ? "Try a different keyword." : isAdmin ? "Add the first course to get started." : "Check back soon — courses will appear here."}
          action={!query && isAdmin && <Button onClick={openCreate}><FiPlus className="h-4 w-4" /> Add course</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(course => {
            const enrolled = course.enrollments?.length || 0;
            return (
              <Card key={course.id} className="group flex flex-col overflow-hidden transition-shadow hover:shadow-soft">
                <div className="relative cursor-pointer" onClick={() => navigate(`/courses/${course.id}`)}>
                  {course.image ? (
                    <img src={uploadUrl(course.image)} alt={course.title} className="h-36 w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-36 w-full items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
                      <FiBookOpen className="h-12 w-12 text-white/60" />
                    </div>
                  )}
                  {course.category && <Badge variant="primary" className="absolute right-3 top-3 bg-white/95">{course.category}</Badge>}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="line-clamp-1 cursor-pointer text-[15px] font-semibold text-slate-900 hover:text-primary-600" onClick={() => navigate(`/courses/${course.id}`)}>{course.title}</h3>
                  <p className="mt-1 line-clamp-2 flex-1 text-sm text-slate-500">{course.description}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-400">
                    {course.duration && <span className="flex items-center gap-1"><FiClock className="h-3.5 w-3.5" /> {course.duration}</span>}
                    {course.tutor && <span className="flex items-center gap-1"><FiUser className="h-3.5 w-3.5" /> {course.tutor}</span>}
                    <span className="flex items-center gap-1"><FiGlobe className="h-3.5 w-3.5" /> {course.languages || "English"}</span>
                    {enrolled > 0 && <span className="ml-auto font-medium text-emerald-600">{enrolled} enrolled</span>}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" className="flex-1" onClick={() => navigate(`/courses/${course.id}`)}>View course</Button>
                    {isAdmin && <>
                      <button onClick={() => openEdit(course)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><FiEdit2 className="h-3.5 w-3.5" /></button>
                      <button onClick={() => handleDelete(course.id)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 text-red-400 hover:bg-red-50"><FiTrash2 className="h-3.5 w-3.5" /></button>
                    </>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={Boolean(modal)} onClose={() => setModal(null)} title={modal === "new" ? "Add a course" : "Edit course"}
        footer={<><Button variant="secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</Button><Button onClick={handleSave} loading={saving}>Save course</Button></>}>
        <div className="space-y-4">
          <Input label="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
          <Textarea label="Description" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Duration" placeholder="e.g. 4 hours" value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} />
            <Input label="Category" placeholder="e.g. Design" value={form.category} onChange={e => setForm({...form, category: e.target.value})} />
            <Input label="Tutor / Instructor" value={form.tutor} onChange={e => setForm({...form, tutor: e.target.value})} />
            <Input label="Language(s)" value={form.languages} onChange={e => setForm({...form, languages: e.target.value})} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Thumbnail image</label>
            <label className="flex h-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-400 hover:border-primary-300 hover:text-primary-500">
              <FiImage className="h-5 w-5" />
              <span className="text-xs">{imageFile ? imageFile.name : "Click to upload"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={e => setImageFile(e.target.files?.[0] || null)} />
            </label>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
